// src/components/currencies/forms/CreateCurrency.tsx

import React, { useEffect, useState } from "react";
import Field from "@/components/shared/ui/Field";
import Input from "@/components/shared/ui/Input";
import { FiHash, FiType, FiFlag } from "react-icons/fi";

export type UpsertCurrency = {
  code: string;
  name: string;
  symbol?: string;
  minorUnit?: string | number | null;
  country?: string;
};

export default function CreateCurrency({
  onSubmit,
  onCancel,
  defaultValues,
}: {
  onSubmit: (payload: UpsertCurrency) => void;
  onCancel?: () => void;
  defaultValues?: Partial<UpsertCurrency>;
}) {
  const [form, setForm] = useState<UpsertCurrency>({
    code: defaultValues?.code ?? "",
    name: defaultValues?.name ?? "",
    symbol: defaultValues?.symbol ?? "",
    minorUnit:
      defaultValues?.minorUnit === undefined ? "" : (defaultValues.minorUnit as any),
    country: defaultValues?.country ?? "",
  });

  // If the modal is reused with different defaults, sync once it opens
  useEffect(() => {
    setForm((p) => ({
      ...p,
      code: defaultValues?.code ?? "",
      name: defaultValues?.name ?? "",
      symbol: defaultValues?.symbol ?? "",
      minorUnit:
        defaultValues?.minorUnit === undefined ? "" : (defaultValues.minorUnit as any),
      country: defaultValues?.country ?? "",
    }));
  }, [
    defaultValues?.code,
    defaultValues?.name,
    defaultValues?.symbol,
    defaultValues?.minorUnit,
    defaultValues?.country,
  ]);

  const change = <K extends keyof UpsertCurrency>(
    k: K,
    v: UpsertCurrency[K]
  ) => setForm((p) => ({ ...p, [k]: v }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();

    const code = (form.code ?? "").trim().toUpperCase();
    const name = (form.name ?? "").trim();

    // Backend enforces 3-letter code; keep FE guard too
    if (!code || code.length !== 3) return;
    if (!name) return;

    onSubmit({
      code,
      name,
      symbol: form.symbol?.trim() || "",
      minorUnit: form.minorUnit ?? "",
      country: form.country?.trim() || "",
    });
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {/* Code */}
      <Field label="Currency code" icon={<FiHash />}>
        <Input
          value={form.code}
          onChange={(e) => change("code", e.target.value)}
          placeholder="e.g., USD, CAD, PKR"
          required
        />
      </Field>

      {/* Name */}
      <Field label="Currency name" icon={<FiType />}>
        <Input
          value={form.name}
          onChange={(e) => change("name", e.target.value)}
          placeholder="e.g., US Dollar, Canadian Dollar"
          required
        />
      </Field>

      {/* Symbol */}
      <Field label="Symbol" icon={<FiType />}>
        <Input
          value={form.symbol ?? ""}
          onChange={(e) => change("symbol", e.target.value)}
          placeholder="e.g., $, CA$, ₹ (optional)"
        />
      </Field>

      {/* Minor unit */}
      <Field label="Minor unit" icon={<FiHash />}>
        <Input
          value={String(form.minorUnit ?? "")}
          onChange={(e) => change("minorUnit", e.target.value)}
          placeholder="e.g., 2 (optional)"
        />
      </Field>

      {/* Country */}
      <Field label="Country" icon={<FiFlag />}>
        <Input
          value={form.country ?? ""}
          onChange={(e) => change("country", e.target.value)}
          placeholder="e.g., United States, Canada (optional)"
        />
      </Field>

      {/* Actions */}
      <div className="mt-2 flex justify-end gap-2 sm:col-span-2">
        <button
          type="button"
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          onClick={() => onCancel?.()}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700"
        >
          Create Currency
        </button>
      </div>
    </form>
  );
}
